import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  ArrowLeft,
  Building2,
  Search,
  Save,
  RefreshCcw,
  CheckSquare,
} from "lucide-react";

const BASE_URL = "https://nissi-india.com"; // change if needed

function toNum(v, def = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
}
function toBool(v) {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v === 1;
  const s = (v ?? "").toString().trim().toLowerCase();
  return s === "true" || s === "1" || s === "yes" || s === "y";
}
function fmtDateISO(d) {
  if (!d) return "";
  const dt = new Date(d);
  if (!Number.isFinite(dt.getTime())) return "";
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function defaultMonthRange() {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { from: fmtDateISO(from), to: fmtDateISO(to) };
}

/**
 * Recalculate row exactly like ASP:
 * - Leave clears all
 * - i = total days (includes half)
 * - i1 = full shifts count
 * - i2 = half shifts count (each half shift increments i2 by 1, but contributes 0.5 to i)
 * - NotPay removes full shifts from i and sets i1 = 0
 * - CLA/HalfCLA uses empClaAllowance
 * - total = (rate * i1) + (halfShiftAmt * i2) + heatAllowance + otAmt
 * - net   = (rate * i1) + (halfShiftAmt * i2) + CLAallow + heatAllowance + otAmt
 */
function recalcRow(row, empClaAllowance) {
  const r = { ...row };

  const rate = toNum(r.rate, 0);

  // normalize numeric fields
  r.noOfHeat = toNum(r.noOfHeat, 0);
  r.heatAllowance = toNum(r.heatAllowance, 0);
  r.otAmt = toNum(r.otAmt, 0);
  r.otHrs = toNum(r.otHrs, 0);
  r.halfShiftAmt = toNum(r.halfShiftAmt, 0);

  const leave = toBool(r.leave);

  if (leave) {
    r.dayShift1 = false;
    r.dayShift2 = false;
    r.nightShift1 = false;
    r.nightShift2 = false;
    r.halfDay = false;
    r.halfNight = false;
    r.heat = false;
    r.ot = false;

    r.notPay = false;
    r.CLA = false;
    r.halfCLA = false;

    r.noOfDays = 0;
    r.totalAmount = 0;
    r.netAmount = 0;

    r.noOfHeat = 0;
    r.heatAllowance = 0;
    r.otAmt = 0;
    r.otHrs = 0;

    r.claNoOfDays = 0;
    r.claAllowance = 0;

    // half shift amt becomes 0 because no half selected
    r.halfShiftAmt = 0;

    return r;
  }

  // compute i, i1, i2
  let i = 0;   // days including half
  let i1 = 0;  // full shifts
  let i2 = 0;  // half shifts count

  const d1 = toBool(r.dayShift1);
  const d2 = toBool(r.dayShift2);
  const n1 = toBool(r.nightShift1);
  const n2 = toBool(r.nightShift2);
  const hd = toBool(r.halfDay);
  const hn = toBool(r.halfNight);

  if (d1) { i += 1; i1 += 1; }
  if (d2) { i += 1; i1 += 1; }
  if (n1) { i += 1; i1 += 1; }
  if (n2) { i += 1; i1 += 1; }

  if (hd) { i += 0.5; i2 += 1; }
  if (hn) { i += 0.5; i2 += 1; }

  // Not Pay rule (ASP: i = i - i1; i1 = 0)
  if (toBool(r.notPay)) {
    i = i - i1;
    i1 = 0;
  }

  // half shift amount: if no half selected => 0
  if (!hd && !hn) {
    r.halfShiftAmt = 0;
  }

  // Heat enable/disable behavior (ASP sets to 0 and disables)
  if (!toBool(r.heat)) {
    r.noOfHeat = 0;
    r.heatAllowance = 0;
  }

  // OT enable/disable behavior
  if (!toBool(r.ot)) {
    r.otAmt = 0;
    r.otHrs = 0;
  }

  // CLA / HalfCLA (mutually exclusive)
  const baseCla = toNum(empClaAllowance, 0);
  let allow = 0;

  if (toBool(r.CLA)) {
    r.halfCLA = false;
    if (baseCla > 0) {
      r.claNoOfDays = 1;
      r.claAllowance = baseCla;
      allow = baseCla;
    } else {
      r.claNoOfDays = 0;
      r.claAllowance = 0;
      allow = 0;
    }
  } else if (toBool(r.halfCLA)) {
    r.CLA = false;
    if (baseCla > 0) {
      r.claNoOfDays = 0.5;
      r.claAllowance = baseCla / 2;
      allow = baseCla / 2;
    } else {
      r.claNoOfDays = 0;
      r.claAllowance = 0;
      allow = 0;
    }
  } else {
    r.claNoOfDays = 0;
    r.claAllowance = 0;
    allow = 0;
  }

  r.noOfDays = i;

  // totals (ASP formula)
  const total =
    (rate * i1) +
    (toNum(r.halfShiftAmt, 0) * i2) +
    toNum(r.heatAllowance, 0) +
    toNum(r.otAmt, 0);

  const net =
    (rate * i1) +
    (toNum(r.halfShiftAmt, 0) * i2) +
    toNum(allow, 0) +
    toNum(r.heatAllowance, 0) +
    toNum(r.otAmt, 0);

  r.totalAmount = total;
  r.netAmount = net;

  return r;
}

const DutyCardViewEdit = () => {
  const navigate = useNavigate();
  const { empId } = useParams();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const loginBranchId = localStorage.getItem("branchId") || ""; // your login branch
  const branchType = localStorage.getItem("branchType") || localStorage.getItem("branchtype") || "HO"; // keep flexible

  const screenBadge = useMemo(() => "INFRA SCREEN : C-17/2", []);

  // UI classes (same style as your template)
  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const disabledBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `bg-gray-400 cursor-not-allowed`;

  // Meta + filters
  const [branches, setBranches] = useState([]);
  const [halfShiftAmounts, setHalfShiftAmounts] = useState([]);

  const [branchId, setBranchId] = useState(loginBranchId || "Select");
const [fromDate, setFromDate] = useState("");
const [toDate, setToDate] = useState("");

  // Data
  const [loading, setLoading] = useState(false);
  const [emp, setEmp] = useState(null);
  const [canEdit, setCanEdit] = useState(true);
  const [rows, setRows] = useState([]);
  const [selectedCardIds, setSelectedCardIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const empClaAllowance = toNum(emp?.claAllowance, 0);

  const loadMeta = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/payroll/dutycard-view/meta`, {
        params: {
          finYearId: Number(finYearId),
          branchId: branchId === "Select" ? loginBranchId : branchId,
          branchType: branchType,
        },
      });

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Meta load failed");
        return;
      }

      setBranches(res.data.branches || []);
      setHalfShiftAmounts(res.data.halfShiftAmounts || []);

      // keep current branch selection if possible
      if (!branchId || branchId === "Select") {
        setBranchId(res.data.defaultBranchId || loginBranchId || "Select");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchDutyCard = async () => {
  if (!String(finYearId).trim()) {
    toast.warn("Please select Financial Year.");
    return;
  }
  if (!empId) {
    toast.error("empId missing in route.");
    return;
  }
  if (!branchId || branchId === "Select") {
    toast.warn("Please select Branch");
    return;
  }

  // ✅ IMPORTANT: do NOT force date selection.
  // If dates are empty, backend will auto-set:
  // fromDate = first record date, toDate = today.
  const hasFrom = !!String(fromDate || "").trim();
  const hasTo = !!String(toDate || "").trim();

  // ASP behavior: if only one date entered -> error
  if ((hasFrom && !hasTo) || (!hasFrom && hasTo)) {
    toast.warn("Please select FromDate and ToDate");
    return;
  }

  try {
    setLoading(true);

    const res = await axios.get(`${BASE_URL}/api/payroll/dutycard-view`, {
      params: {
        finYearId: Number(finYearId),
        empId: Number(empId),
        branchId: Number(branchId),
        branchType: String(branchType),

        // ✅ only send if present (else Node defaults will work)
        ...(hasFrom ? { fromDate } : {}),
        ...(hasTo ? { toDate } : {}),
      },
    });

    if (!res?.data?.success) {
      toast.error(res?.data?.message || "Failed to load duty card");
      return;
    }

    // ✅ set default dates returned by server (min-date → today)
    if (res.data.fromDate) setFromDate(fmtDateISO(res.data.fromDate));
    if (res.data.toDate) setToDate(fmtDateISO(res.data.toDate));

    setEmp(res.data.emp || null);
    setCanEdit(res.data.canEdit !== false);

    const apiRows = res.data.rows || [];

    const normalized = apiRows.map((r) => {
      const row = {
        cardId: r.fld_cardid,
        employeeId: r.fld_employeeid,

        date: r.fld_date,
        branchName: r.fld_branchname,

        dcardNo: r.fld_dcardno ?? "",

        leave: toBool(r.fld_leave),
        dayShift1: toBool(r.fld_dayshift1),
        dayShift2: toBool(r.fld_dayshift2),
        nightShift1: toBool(r.fld_nightshift1),
        nightShift2: toBool(r.fld_nightshift2),
        halfDay: toBool(r.fld_halfday),
        halfNight: toBool(r.fld_halfnight),

        notPay: toBool(r.fld_notpay),

        CLA: toBool(r.fld_CLA),
        halfCLA: toBool(r.fld_halfCLA),

        heat: toBool(r.fld_heat),
        noOfHeat: toNum(r.fld_noofheat, 0),
        heatAllowance: toNum(r.fld_heatallowance, 0),

        ot: toBool(r.fld_ot),
        otHrs: toNum(r.fld_othrs, 0),
        otAmt: toNum(r.fld_otamt, 0),

        noOfDays: toNum(r.fld_noofdays, 0),
        rate: toNum(r.fld_dailybata, 0),
        totalAmount: toNum(r.fld_totalamount, 0),
        claNoOfDays: toNum(r.fld_CLAnoofdays, 0),
        claAllowance: toNum(r.fld_CLAallowance, 0),
        netAmount: toNum(r.fld_Netamount, 0),

        remark: r.fld_remark ?? "",

        halfShiftAmt: toNum(r.fld_halfshiftamt, 0),

        _selected: false,
      };

      return recalcRow(row, empClaAllowance);
    });

    setRows(normalized);
    setSelectedCardIds([]);
    setSelectAll(false);

    if (normalized.length === 0) toast.info("No Records Found");
  } catch (e) {
    toast.error(e?.response?.data?.message || e.message);
  } finally {
    setLoading(false);
  }
};


  useEffect(() => {
    loadMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // auto load dutycard once meta is loaded and we have empId
  useEffect(() => {
    if (empId && branchId && branchId !== "Select" && String(finYearId).trim()) {
      fetchDutyCard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empId]);

  const toggleSelectAll = () => {
    if (selectAll) {
      setRows((prev) => prev.map((r) => ({ ...r, _selected: false })));
      setSelectedCardIds([]);
      setSelectAll(false);
      return;
    }
    setRows((prev) => prev.map((r) => ({ ...r, _selected: true })));
    setSelectedCardIds(rows.map((r) => r.cardId));
    setSelectAll(true);
  };

  const toggleRowSelect = (cardId) => {
    setRows((prev) => {
      const next = prev.map((r) =>
        r.cardId === cardId ? { ...r, _selected: !r._selected } : r
      );
      const selected = next.filter((x) => x._selected).map((x) => x.cardId);
      setSelectedCardIds(selected);
      setSelectAll(selected.length > 0 && selected.length === next.length);
      return next;
    });
  };

  const updateRowField = (cardId, patch) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.cardId !== cardId) return r;
        const merged = { ...r, ...patch };

        // if half shift dropdown changes and any half is selected, reflect it immediately
        const recalced = recalcRow(merged, empClaAllowance);
        return recalced;
      })
    );
  };

  const handleSearch = async (e) => {
    e?.preventDefault?.();

    if (!branchId || branchId === "Select") {
      toast.warn("Please select Branch");
      return;
    }
    if (!fromDate || !toDate) {
      toast.warn("Please select FromDate and ToDate");
      return;
    }
    await fetchDutyCard();
  };

  const handleSubmitSelected = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!canEdit) {
      toast.warn("You cannot edit for this branch selection.");
      return;
    }

    const selected = rows.filter((r) => r._selected);
    if (selected.length === 0) {
      toast.warn("Please select any one");
      return;
    }

    const ok = window.confirm("Are you sure to submit?");
    if (!ok) return;

    try {
      setLoading(true);

      const payload = selected.map((r) => ({
        cardId: r.cardId,
        dcardNo: r.dcardNo ?? "",

        leave: toBool(r.leave),
        dayShift1: toBool(r.dayShift1),
        dayShift2: toBool(r.dayShift2),
        nightShift1: toBool(r.nightShift1),
        nightShift2: toBool(r.nightShift2),
        halfDay: toBool(r.halfDay),
        halfNight: toBool(r.halfNight),

        notPay: toBool(r.notPay),
        CLA: toBool(r.CLA),
        halfCLA: toBool(r.halfCLA),

        noOfDays: toNum(r.noOfDays, 0),
        totalAmount: toNum(r.totalAmount, 0),
        claNoOfDays: toNum(r.claNoOfDays, 0),
        claAllowance: toNum(r.claAllowance, 0),
        netAmount: toNum(r.netAmount, 0),

        remark: r.remark ?? "",

        halfShiftAmt: toNum(r.halfShiftAmt, 0),

        heat: toBool(r.heat),
        heatAllowance: toNum(r.heatAllowance, 0),
        noOfHeat: toNum(r.noOfHeat, 0),

        ot: toBool(r.ot),
        otAmt: toNum(r.otAmt, 0),
        otHrs: toNum(r.otHrs, 0),
      }));

      const res = await axios.post(
        `${BASE_URL}/api/payroll/dutycard-view/update-many`,
        { rows: payload },
        { params: { finYearId: Number(finYearId) } }
      );

      if (res?.data?.success) {
        toast.success(res?.data?.message || "Updated Successfully");
        // mimic ASP: go back to listing
        navigate(-1);
      } else {
        toast.error(res?.data?.message || "Update failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  const tableTh = `px-3 py-3 text-xs font-extrabold uppercase tracking-wider text-center whitespace-nowrap`;
  const tableTd = `px-3 py-2 text-sm text-center whitespace-nowrap ${isDark ? "text-gray-200" : "text-gray-800"}`;

  const smallInput =
    `w-24 px-2 py-2 rounded-xl border outline-none text-sm ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 focus:ring-2 focus:ring-blue-500");

  const tinyInput =
    `w-20 px-2 py-2 rounded-xl border outline-none text-sm ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 focus:ring-2 focus:ring-blue-500");

  const selectBase =
    `px-2 py-2 rounded-xl border outline-none text-sm ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 focus:ring-2 focus:ring-blue-500");

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">DUTY CARD VIEW & UPDATE</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                <Building2 className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Bata View & Update
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  View + Edit in one screen (ASP logic)
                </div>
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {screenBadge}
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"}`}>
                {rows.length} Rows
              </div>

              {!canEdit && (
                <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-red-900/40 text-red-100" : "bg-red-100 text-red-800"}`}>
                  Edit Disabled (Employee Branch ≠ Selected Branch)
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={() => navigate(-1)} className={ghostBtn} disabled={loading}>
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>

              <button onClick={loadMeta} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Reload Meta
              </button>

              <button onClick={fetchDutyCard} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh Data
              </button>
            </div>
          </div>

          {/* Search Panel (Branch + Dates) */}
          <form onSubmit={handleSearch} className={cardBase + " p-6 mb-6"}>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
              <div>
                <label className={labelBase}>
                  Branch <span className="text-red-500">*</span>
                </label>
                <select
                  className={inputBase}
                  value={branchId || "Select"}
                  onChange={(e) => setBranchId(e.target.value)}
                  disabled={loading}
                >
                  <option value="Select">Select</option>
                  {branches.map((b) => (
                    <option key={String(b.id)} value={String(b.id)}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelBase}>
                  FromDate <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  className={inputBase}
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div>
                <label className={labelBase}>
                  ToDate <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  className={inputBase}
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className="flex items-end">
                <button type="submit" className={primaryBtn} disabled={loading}>
                  <Search className="w-4 h-4" />
                  Search
                </button>
              </div>
            </div>

            {/* Employee header */}
            {emp && (
              <div className={`mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 rounded-2xl p-4 ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
                <div className="text-sm">
                  <span className={`font-extrabold ${isDark ? "text-gray-100" : "text-gray-900"}`}>Employee Code:</span>{" "}
                  <span className="font-bold">{emp.employeeCode || "-"}</span>
                </div>
                <div className="text-sm">
                  <span className={`font-extrabold ${isDark ? "text-gray-100" : "text-gray-900"}`}>Employee Name:</span>{" "}
                  <span className="font-bold">{emp.employeeName || "-"}</span>
                </div>
              </div>
            )}
          </form>

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className={tableTh}>S.No</th>
                    <th className={tableTh}>Date</th>
                    <th className={tableTh}>Branch</th>
                    <th className={tableTh}>D.Card No</th>

                    <th className={tableTh}>Leave</th>
                    <th className={tableTh}>D1</th>
                    <th className={tableTh}>D2</th>
                    <th className={tableTh}>N1</th>
                    <th className={tableTh}>N2</th>

                    <th className={tableTh}>HalfDay</th>
                    <th className={tableTh}>HalfNight</th>

                    <th className={tableTh}>N/P</th>
                    <th className={tableTh}>CLA</th>
                    <th className={tableTh}>HalfCLA</th>

                    <th className={tableTh}>Heat</th>
                    <th className={tableTh}>NoHeat</th>
                    <th className={tableTh}>HeatAmt</th>

                    <th className={tableTh}>OT</th>
                    <th className={tableTh}>OT Hrs</th>
                    <th className={tableTh}>OTAmt</th>

                    <th className={tableTh}>HalfShiftAmt</th>

                    <th className={tableTh}>NoDays</th>
                    <th className={tableTh}>Rate</th>
                    <th className={tableTh}>Total</th>
                    <th className={tableTh}>CLA Days</th>
                    <th className={tableTh}>CLA Allow</th>
                    <th className={tableTh}>Net</th>
                    <th className={tableTh}>Remarks</th>

                    <th className={tableTh}>
                      <div className="flex items-center justify-center gap-2">
                        Select All
                        <input type="checkbox" checked={selectAll} onChange={toggleSelectAll} />
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: 6 }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        <td colSpan={29} className="px-4 py-6">
                          <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                        </td>
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={29} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => {
                      const disabled = !canEdit;

                      return (
                        <tr key={String(r.cardId)} className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                          <td className={tableTd}>{idx + 1}</td>
                          <td className={tableTd}>{r.date ?? ""}</td>
                          <td className={tableTd}>{r.branchName ?? ""}</td>

                          <td className={tableTd}>
                            <input
                              className={`${smallInput} ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
                              value={r.dcardNo}
                              onChange={(e) => updateRowField(r.cardId, { dcardNo: e.target.value })}
                              disabled={disabled}
                            />
                          </td>

                          {/* checkboxes */}
                          {[
                            ["leave", "leave"],
                            ["dayShift1", "dayShift1"],
                            ["dayShift2", "dayShift2"],
                            ["nightShift1", "nightShift1"],
                            ["nightShift2", "nightShift2"],
                            ["halfDay", "halfDay"],
                            ["halfNight", "halfNight"],
                            ["notPay", "notPay"],
                            ["CLA", "CLA"],
                            ["halfCLA", "halfCLA"],
                            ["heat", "heat"],
                          ].map(([key, label]) => (
                            <td key={key} className={tableTd}>
                              <input
                                type="checkbox"
                                checked={toBool(r[key])}
                                onChange={(e) => updateRowField(r.cardId, { [key]: e.target.checked })}
                                disabled={disabled}
                              />
                            </td>
                          ))}

                          {/* heat fields */}
                          <td className={tableTd}>
                            <input
                              className={`${tinyInput} ${disabled || !toBool(r.heat) ? "opacity-60 cursor-not-allowed" : ""}`}
                              value={String(r.noOfHeat ?? 0)}
                              onChange={(e) => updateRowField(r.cardId, { noOfHeat: e.target.value })}
                              disabled={disabled || !toBool(r.heat)}
                            />
                          </td>
                          <td className={tableTd}>
                            <input
                              className={`${tinyInput} ${disabled || !toBool(r.heat) ? "opacity-60 cursor-not-allowed" : ""}`}
                              value={String(r.heatAllowance ?? 0)}
                              onChange={(e) => updateRowField(r.cardId, { heatAllowance: e.target.value })}
                              disabled={disabled || !toBool(r.heat)}
                            />
                          </td>

                          {/* OT */}
                          <td className={tableTd}>
                            <input
                              type="checkbox"
                              checked={toBool(r.ot)}
                              onChange={(e) => updateRowField(r.cardId, { ot: e.target.checked })}
                              disabled={disabled}
                            />
                          </td>
                          <td className={tableTd}>
                            <input
                              className={`${tinyInput} ${disabled || !toBool(r.ot) ? "opacity-60 cursor-not-allowed" : ""}`}
                              value={String(r.otHrs ?? 0)}
                              onChange={(e) => updateRowField(r.cardId, { otHrs: e.target.value })}
                              disabled={disabled || !toBool(r.ot)}
                            />
                          </td>
                          <td className={tableTd}>
                            <input
                              className={`${tinyInput} ${disabled || !toBool(r.ot) ? "opacity-60 cursor-not-allowed" : ""}`}
                              value={String(r.otAmt ?? 0)}
                              onChange={(e) => updateRowField(r.cardId, { otAmt: e.target.value })}
                              disabled={disabled || !toBool(r.ot)}
                            />
                          </td>

                          {/* HalfShift amount dropdown (amount values) */}
                          <td className={tableTd}>
                            <select
                              className={`${selectBase} ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
                              value={String(toNum(r.halfShiftAmt, 0))}
                              onChange={(e) => updateRowField(r.cardId, { halfShiftAmt: e.target.value })}
                              disabled={disabled}
                            >
                              <option value="0">0</option>
                              {halfShiftAmounts.map((hs) => (
                                <option key={String(hs.id)} value={String(hs.amount)}>
                                  {hs.label}
                                </option>
                              ))}
                            </select>
                          </td>

                          {/* readonly computed */}
                          <td className={tableTd}>{toNum(r.noOfDays, 0)}</td>
                          <td className={tableTd}>{toNum(r.rate, 0)}</td>
                          <td className={tableTd}>{toNum(r.totalAmount, 0)}</td>
                          <td className={tableTd}>{toNum(r.claNoOfDays, 0)}</td>
                          <td className={tableTd}>{toNum(r.claAllowance, 0)}</td>
                          <td className={tableTd}>{toNum(r.netAmount, 0)}</td>

                          {/* remarks */}
                          <td className={tableTd}>
                            <input
                              className={`w-56 px-2 py-2 rounded-xl border outline-none text-sm ${
                                isDark
                                  ? "bg-gray-800/40 border-white/10 text-white"
                                  : "bg-white border-gray-200 text-gray-900"
                              } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
                              value={r.remark ?? ""}
                              onChange={(e) => updateRowField(r.cardId, { remark: e.target.value })}
                              disabled={disabled}
                            />
                          </td>

                          {/* select */}
                          <td className={tableTd}>
                            <input
                              type="checkbox"
                              checked={!!r._selected}
                              onChange={() => toggleRowSelect(r.cardId)}
                              disabled={disabled}
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Footer actions */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 p-5">
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Selected: <b>{selectedCardIds.length}</b>
              </div>

              <div className="flex flex-wrap items-center gap-3 justify-end">
                <button
                  type="button"
                  onClick={handleSubmitSelected}
                  className={(!canEdit || selectedCardIds.length === 0) ? disabledBtn : primaryBtn}
                  disabled={loading || !canEdit || selectedCardIds.length === 0}
                  title={!canEdit ? "Edit disabled for this branch" : ""}
                >
                  <Save className="w-4 h-4" />
                  Submit Selected
                </button>

                <button
                  type="button"
                  onClick={toggleSelectAll}
                  className={ghostBtn}
                  disabled={loading || !canEdit || rows.length === 0}
                >
                  <CheckSquare className="w-4 h-4" />
                  Toggle Select All
                </button>
              </div>
            </div>
          </div>

          {/* Small note about CLA */}
          {emp && (
            <div className={`mt-5 text-xs ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              CLA base allowance for this employee: <b>{empClaAllowance}</b> (used for CLA/HalfCLA calculations like ASP).
            </div>
          )}
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default DutyCardViewEdit;
